import React, {createContext, useEffect } from 'react';
import {Auth0Provider, useAuth0, Auth0ContextInterface} from "@auth0/auth0-react";
import { Route, Link, useLocation, useNavigate, Routes} from "react-router-dom";
import axios, { AxiosInstance } from "axios";
import {config} from "../config";
import { Button } from "antd";
import Cookies from 'js-cookie';
import {
    Href,
    Location,
    LocationDescriptor, LocationDescriptorObject,
    LocationListener,
    LocationState,
    Path,
    TransitionPromptHook,
    UnregisterCallback
} from "history";
import {
    LDProvider, useFlags,
    useLDClient,
    withLDProvider
} from 'launchdarkly-react-client-sdk';
import NativeSocket from "./sockets/NativeSocket";
import { default as io, Socket } from "./sockets/SocketIOSocket";
import ProgressManager from "./user/Progress.feedback";
import HoldingPage from '../components/navbar/HoldingPage';
import LogoutPage from 'auth/login/Logout';
import { verifyToken } from "auth/authHelpers";
import { startSessionRetry } from "auth/sessionManager";
import { useSessionManager } from "auth/useSessionManager";

export interface History<HistoryLocationState = LocationState> {
    length: number;
    action: Action;
    location: Location<HistoryLocationState>;
    push(path: Path, state?: HistoryLocationState): void;
    push(location: LocationDescriptor<HistoryLocationState>): void;
    replace(path: Path, state?: HistoryLocationState): void;
    replace(location: LocationDescriptor<HistoryLocationState>): void;
    go(n: number): void;
    goBack(): void;
    goForward(): void;
    block(prompt?: boolean | string | TransitionPromptHook<HistoryLocationState>): UnregisterCallback;
    listen(listener: LocationListener<HistoryLocationState>): UnregisterCallback;
    createHref(location: LocationDescriptorObject<HistoryLocationState>): Href;
}

export interface UserCustomerApplicationDetail {
    id: string,
    name: string, 
    description: string,
    internal: boolean
}

export interface UserAssignedDashboard {
    id: string, 
    name: string,
    route: string,
    customerApplication: string
}

interface UserDetails {
    email: string;
    email_verified: boolean;
    family_name: string;
    given_name: string;
    firstName: string;
    nickname: string;
    picture: string;
    updated_at: Date;
    permissions?: any;
    customerApplications?: string[];
    customerApplicationDetails?: UserCustomerApplicationDetail[];
    assignedDashboards?: UserAssignedDashboard[];
    targetLanguages?: string[];
}

export interface UserContext {
    accessToken?: string;
    MASToken?:string;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
    userDetails: UserDetails;
    isAdmin: boolean;
    timezone: string;
    baseUrl: string;
    returnTo?:string;
    loginWithRedirect?: () => void;
    client: AxiosInstance;
    legacyClient: AxiosInstance,
    gpClient: AxiosInstance,
    umsClient: AxiosInstance,
    coreClient: AxiosInstance,
    invoicingClient: AxiosInstance,
    umsSocket: Record<string, Socket>,
    gpSocket: Record<string, Socket>,
    // chSocket: Record<string, Socket>,
    reportSocket: Record<string, Socket>,
    oliveSocket: Record<string, Socket>,
    invoicingSocket: Record<string, Socket>,
    applicationSettingsSocket: Record<string, Socket>,
    useAuth0: Auth0ContextInterface;
    socket: NativeSocket;
    socketInit?:boolean;
    vapid_key?:string;
    history: History;
    navigate: any;
    location: Location,
    LD: LDProvider;
    LD_Ready: boolean;
    logout: () => void;
    loginRequired: boolean;
    setToken: ( token:string ) => void;
    verifyToken: ( token:string ) => boolean;
    removeToken: ( ) => void;
    state:any;
    dispatch:any;
    flags:Record<any, any>
    progress: Map<string, any>;
    progressManager: ProgressManager;
    selectedApp?: string;
    setSelectedApp?: (appId: string) => void;
    applicationSettings?: Record<string, any>;
    sessionExpired?: boolean;
}

export enum ActionTypes {
    UPDATE_TOKEN="UPDATE_TOKEN",
    DELETE_TOKEN="DELETE_TOKEN",
    UPDATE_IS_AUTH="UPDATE_IS_AUTH",
    UPDATE_IS_AUTHLOADING="UPDATE_IS_AUTHLOADING",
    UPDATE_USER_DETAILS="UPDATE_USER_DETAILS",
    UPDATE_IS_ADMIN="UPDATE_IS_ADMIN",
    UPDATE_TIMEZONE="UPDATE_TIMEZONE",
    UPDATE_BASE_URL="UPDATE_BASE_URL",
    ATTACH_LOGIN_METHOD="ATTACH_LOGIN_METHOD",
    ATTACH_SOCKET="ATTACH_SOCKET",
    SOCKET_INIT="SOCKET_INIT",
    VAPID_KEY="VAPID_KEY",
    UPDATE_NOTIFICATIONS="UPDATE_NOTIFICATIONS",
    ATTACH_HISTORY="ATTACH_HISTORY",
    ATTACH_LD="ATTACH_LD",
    LD_READY="LD_READY",
    UPDATE_FLAGS="UPDATE_FLAGS",
    LOGIN_REQUIRED="LOGIN_REQUIRED",
    UPDATE_PROGRESS="UPDATE_PROGRESS",
    CREATE_PROGRESS_MANAGER="CREATE_PROGRESS_MANAGER",
    SET_SELECTED_APP = "SET_SELECTED_APP",
    UPDATE_APPLICATION_SETTINGS= "UPDATE_APPLICATION_SETTINGS",
    SESSION_EXPIRED="SESSION_EXPIRED",
    SESSION_RESTORED="SESSION_RESTORED",
}

export interface Action {
    type: ActionTypes;
    payload?: any;
    notifier?: any;
    history?: any;
    navigate?: any;
}

type Context = UserContext|{};

const defaultDispatch: React.Dispatch<Action> = () => {};
const MercuryContext = createContext<Partial<UserContext>>( {
    state: { userDetails: {}, progress: new Map() },
    dispatch: defaultDispatch
} );

function defaultClient(
    login: () => void,
    API_URL: string,
    token: string,
    _dispatch?: any,
    _getState?: () => any
) {
    const instance = axios.create();

    instance.defaults.baseURL = API_URL;
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    instance.defaults.headers.common['Content-Type'] = "application/json";

    // Keep token header aligned with latest cookie value.
    instance.interceptors.request.use(config => {
        const activeToken = Cookies.get("mercury_shared_token");
        if (activeToken) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${activeToken}`;
        }

        return config;
    });

    // Session warning modal is timer-driven, not request-failure-driven.
    instance.interceptors.response.use(
        res => res,
        error => Promise.reject(error)
    );

    return instance;
}

function urlBase64ToUint8Array(base64String:string):Uint8Array {
    const
        padding = "=".repeat((4 - base64String.length % 4) % 4),
        base64 = (base64String + padding)
            .replace(/-/g, "+")
            .replace(/_/g, "/"),
        rawData = window.atob(base64),
        outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

function Subscribe( register:ServiceWorkerRegistration, vapidKey:string, socket:NativeSocket ) {
    if ( !register.pushManager ) {
        // Apple has a self registering push api, which we wont bother with for now.
        return;
    }
    register.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
    })
    .then( ( subscription) => {
        socket.send( JSON.stringify({ action: "subscribe", payload: subscription.toJSON() }) )
    })
    .catch( console.error )
}

function storeReducer(state:Partial<UserContext>, action:Action ):Context {

    let
        progress = state.progress ?? new Map();

    switch (action.type) {
        case 'UPDATE_TOKEN':
            return {
                ...state,
                accessToken: action.payload,
                isAuthLoading: false,
                socketInit: true,
                loginRequired: false
            };
        case 'DELETE_TOKEN':
            return {
                ...state,
                accessToken: undefined,
                loginRequired: true,
                isAuthenticated: false
            };
        case 'UPDATE_IS_AUTH':
            return {
                ...state,
                isAuthenticated: action.payload,
            };
        case 'UPDATE_IS_AUTHLOADING':
            return {
                ...state,
                isAuthLoading: action.payload,
            };
        case 'UPDATE_USER_DETAILS':

            return {
                ...state,
                isAdmin: action.payload.admin,
                userDetails: action.payload,
                isAuthLoading: false,
                isAuthenticated: true
            };
        case 'UPDATE_NOTIFICATIONS':
            const
                ordered = action.payload.filter( ( n:any ) => n !== null ).sort( ( a:any, b:any ) => {
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                } ),
                [ latest ] = ordered;

            if ( latest && action.notifier) {
                action.notifier.info( {
                    key: latest._id,
                    message: latest.title,
                    description: latest.body,
                    duration: 500,
                    btn: (
                            <Button type="primary" size="small" onClick={() => {
                                if( state.navigate ) {
                                    action.notifier.close()
                                    state.navigate( latest.url );
                                }
                            }}>
                                Show me
                            </Button>
                    )
                })
            }

            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    notifications: ordered
                }
            };
        case 'UPDATE_IS_ADMIN':
            return {
                ...state,
                isAdmin: action.payload,
            };
        case 'UPDATE_TIMEZONE':
            return {
                ...state,
                timezone: action.payload,
            };
        case 'UPDATE_BASE_URL':
            return {
                ...state,
                baseUrl: action.payload,
            };

        case 'ATTACH_LOGIN_METHOD':
        case 'ATTACH_SOCKET':
            return {
                ...state,
                ...action.payload,
            };
        case 'SOCKET_INIT':
            return {
                ...state,
                socketInt: false,
            };
        case 'ATTACH_HISTORY':
            return {
                ...state,
                ...action.payload
            };
        case "ATTACH_LD":

            return {
                ...state,
                LD:action.payload.LD
            }
            // break;
        case "LD_READY":

            return {
                ...state,
                LD_Ready: true
            }
        case "UPDATE_FLAGS":
            return {
                ...state,
                flags: action.payload
            }
        case 'LOGIN_REQUIRED':
            return {
                ...state,
                loginRequired: action.payload
            };
        case 'VAPID_KEY':

            navigator.serviceWorker.register('/worker.js', {
                    scope: '/'
                })
                .then( ( register ) => {
                    let serviceWorker;
                    if (register.installing) {
                        serviceWorker = register.installing;
                    } else if (register.waiting) {
                        serviceWorker = register.waiting;
                    } else if (register.active) {
                        serviceWorker = register.active;
                    }

                    if (serviceWorker) {
                        if (serviceWorker.state === "activated") {
                            Subscribe( register, action.payload, state.socket!)
                        }
                        serviceWorker.addEventListener("statechange", (e:any) => {
                            if (e.target.state === "activated") {
                                Subscribe( register, action.payload, state.socket!)
                            }
                        });
                    }
                })
            return {
                ...state,
                vapid_key: action.payload,
            };

        case 'CREATE_PROGRESS_MANAGER':
            return {
                ...state,
                progressManager: action.payload,
            }

        case 'UPDATE_PROGRESS':

            progress.set( action.payload.requestId, action.payload );

            return {
                ...state,
                progress,
            };

        case 'UPDATE_APPLICATION_SETTINGS':
            return {
                ...state,
                applicationSettings: {
                    ...state.applicationSettings,
                    ...action.payload
                }
            }

        case ActionTypes.SET_SELECTED_APP:
            return {
                ...state,
                selectedApp: action.payload,
            };

        case "SESSION_EXPIRED":
            return {
                ...state,
                sessionExpired: true,
                loginRequired: false, // prevent auto-redirect loop
            };

        case "SESSION_RESTORED":
            startSessionRetry();
            return {
                ...state,
                sessionExpired: false,
            };


        default:
            return state;
    }
}

function ContextProvider(props:React.PropsWithChildren<any>) {

    const
        removeToken = () => {
            Cookies.remove('mercury_shared_token')
            dispatch({ type: ActionTypes.DELETE_TOKEN } )
        },
        setToken = ( token:string ) => {
            try {
                console.log("[setToken] Verifying token...", token ? token.substring(0, 20) + "..." : "null");
                if ( verifyToken( token ) ) {
                    console.log("[setToken] Token valid, setting cookie...");
                    Cookies.set('mercury_shared_token', token )
                    console.log("[setToken] Cookie set, dispatching UPDATE_TOKEN...");
                    dispatch({ type: ActionTypes.UPDATE_TOKEN, payload: token } )
                    console.log("[setToken] UPDATE_TOKEN dispatched");
                }
                else {
                    console.warn('[setToken] Token verification failed, calling removeToken');
                    removeToken();
                }

            } catch (error) {
                console.error('[setToken] Token not valid, it will be removed. Redirect to login page.', error)
                removeToken();
            }
        },
        token = Cookies.get('mercury_shared_token'),
        { isAuthenticated, loginWithRedirect, logout } = useAuth0(),
        [_state, dispatch] = React.useReducer<React.Reducer<Context, Action>>(storeReducer, {
            setToken,
            removeToken,
            verifyToken,
            MASToken: token,
            applicationSettings: {}
        }),
        state = _state as UserContext,
        { Loader } = props,
        navigate = useNavigate(),
        location = useLocation(),
        flags = useFlags(),
        LDClient = useLDClient(),
        loggedOut = Cookies.get("logged_out" );

        // Warns users when their session is about to expire.
        // Allows them to manually extend or expire the session via a modal.
        // Automatically extends the session when the user is active and the token is close to expiry.
        // Ensures only one browser tab shows the modal (the leader tab).
        // Ensures all other tabs (followers) listen and react without showing their own modal.
        // Uses a heartbeat mechanism so leadership is reliable even if a tab crashes or closes.
        // Uses jittered token refreshes to avoid multiple tabs refreshing at the same moment.
        // Ensures jitter never pushes refresh past the token's expiry time.
        useSessionManager({
            dispatch,
            setToken,
            removeToken,
            accessToken: state.accessToken,
            sessionExpired: Boolean((state as any)?.sessionExpired),
        });

    const setSelectedApp = (appId: string) => {
        dispatch({ type: ActionTypes.SET_SELECTED_APP, payload: appId });
    };

    if ( loggedOut ) {
        Cookies.remove("logged_out" );
        removeToken();
    }

    useEffect(() => {
        if (state.userDetails?.customerApplications?.length) {
          const apps = state.userDetails.customerApplications;
      
          // try restore from localStorage
          const storedApp = localStorage.getItem("selectedCA");
      
          if (storedApp && apps.includes(storedApp)) {
            if (state.selectedApp !== storedApp) {
              dispatch({ type: ActionTypes.SET_SELECTED_APP, payload: storedApp });
            }
            return;
          }
      
          // fallback to first if no localStorage match
          if (!state.selectedApp) {
            dispatch({ type: ActionTypes.SET_SELECTED_APP, payload: apps[0] });
          }
        }
      }, [state.userDetails, state.selectedApp, dispatch]);

    useEffect( () => {
        dispatch({ type: ActionTypes.UPDATE_FLAGS, payload: flags })
    }, [ flags ] );

    if (
        !state.accessToken &&
        !state.loginRequired &&
        !verifyToken(token || "")
    ) {
        dispatch({ type: ActionTypes.ATTACH_HISTORY, payload: { navigate, location, logout, removeToken }})
        dispatch({ type: ActionTypes.LOGIN_REQUIRED, payload: true});
    }

    if ( state.loginRequired ) {
        return (
            <Auth0Wrapper {...props }>
                <MercuryContext.Provider
                    value={{
                        state: { ...state, selectedApp: state.selectedApp },
                        dispatch,
                        setSelectedApp,
                    }}
                    {...props}
                />
            </Auth0Wrapper>
        )
    }

    if ( state && !state.accessToken ) {
        if ( verifyToken(token || "") ) {

            dispatch({ type: ActionTypes.ATTACH_HISTORY, payload: { navigate, location, logout }})
            dispatch({ type: ActionTypes.UPDATE_BASE_URL, payload: props.REACT_APP_BASE_URL } );
            dispatch({ type: ActionTypes.UPDATE_TOKEN, payload: token });

            return (
                <Auth0Wrapper {...props }>
                    <Loader logo={true} animate={true} title={"Restoring session..."} />
                </Auth0Wrapper>
            )
        }

        if ( !state.isAuthLoading ) {
            dispatch({ type: ActionTypes.ATTACH_HISTORY, payload: { navigate, location, logout }})
            dispatch({ type: ActionTypes.UPDATE_IS_AUTHLOADING, payload: true });
            dispatch({ type: ActionTypes.UPDATE_BASE_URL, payload: props.REACT_APP_BASE_URL } );
        }

        return (
            <Auth0Wrapper {...props }>
                <Loader logo={true} animate={true} title={"Getting access token..."} />
            </Auth0Wrapper>
        )
    }

    if( !state.socket && state.socketInit && state.accessToken  ) {

        try {

            const socket = io.create(
                "/ums-v1/ws/socket.io",
                token!,
                "user"
            )

            const ums = {
                user: socket,
                comment: io.create(
                    "/ums-v1/ws/socket.io",
                    token!,
                    "comment"
                )
            }

            dispatch( { type:ActionTypes.ATTACH_SOCKET, payload: { socket } } );

            const
                gps = {
                    project: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "project"
                    ),
                    document: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "document"
                    ),
                    plp: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "plp"
                    ),
                    adj: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "adjustment"
                    ),
                    filterStorage: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "filterStorage"
                    ),
                    sharing: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "sharing"
                    ),
                    linguist: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "linguist"
                    ),
                    manualproject: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "manualproject"
                    ),
                    queries: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "QueryInformation"
                    ),
                    sesame: io.create(
                        "/google-gp-v1/ws/socket.io",
                        token!,
                        "SesameAccounts"
                    ),
                },
                is = {
                    invoice: io.create(
                        "/invoicing-v1/ws/socket.io",
                        token!,
                        "invoicing"
                    ),
                    var: io.create(
                        "/invoicing-v1/ws/socket.io",
                        token!,
                        "var"
                    ),
                    adjustments: io.create(
                        "/invoicing-v1/ws/socket.io",
                        token!,
                        "adjustments"
                    ),
                },
                // chs = {
                //     captionHub : io.create(
                //         "/core-ch-v1/ws/socket.io",
                //         token!,
                //         "captionHub"
                //     )
                // },
                reports = {
                    queuedReports: io.create(
                        "/reporting-v1/ws/socket.io",
                        token!,
                        "reporting"
                    )
                },
                os = {
                    script: io.create(
                        "/olive-v1/ws/socket.io",
                        token!,
                        "script"
                    ),
                    batch: io.create(
                        "/olive-v1/ws/socket.io",
                        token!,
                        "batch"
                    ),
                    reviewSession: io.create(
                        "/olive-v1/ws/socket.io",
                        token!,
                        "reviewsession"
                    )
                },
                ss = {
                    settings: io.create(
                        "/core-v1/ws/socket.io",
                        token!,
                        "application-settings"
                    )
                }

            ss.settings.on("settings-snapshot", (settings: Record<string, any>) => {
                dispatch({
                    type: ActionTypes.UPDATE_APPLICATION_SETTINGS,
                    payload: settings
                });
            });

            ss.settings.on("setting-updated", ({ key, value, metadata }) => {
                dispatch({
                    type: ActionTypes.UPDATE_APPLICATION_SETTINGS,
                    payload: {
                        [key]: {
                            value,
                            ...metadata
                        }
                    }
                });
            });

            socket.on( "update-user-details", ( payload:any ) => {

                    dispatch({ type: ActionTypes.UPDATE_BASE_URL, payload: props.REACT_APP_BASE_URL } );
                    dispatch({
                        type:ActionTypes.ATTACH_LOGIN_METHOD,
                        payload: {
                            loginWithRedirect,
                            client: defaultClient(loginWithRedirect, props.REACT_APP_API_URL, state.accessToken!, dispatch, () => state),
                            legacyClient: defaultClient(loginWithRedirect, props.REACT_APP_BASE_URL, state.accessToken!, dispatch, () => state),
                            gpClient: defaultClient(loginWithRedirect, config.IS_DEV ? "http://localhost:4000" : props.REACT_APP_SERVICES_API_URL, state.accessToken!, dispatch, () => state),
                            umsClient: defaultClient(loginWithRedirect, config.IS_DEV ? "http://localhost:4000" : props.REACT_APP_SERVICES_API_URL, state.accessToken!, dispatch, () => state),
                            coreClient: defaultClient(loginWithRedirect, config.IS_DEV ? "http://localhost:4000" : props.REACT_APP_SERVICES_API_URL, state.accessToken!, dispatch, () => state),
                            invoicingClient: defaultClient(loginWithRedirect, config.IS_DEV ? "http://localhost:4000" : props.REACT_APP_SERVICES_API_URL, state.accessToken!, dispatch, () => state),
                            gpSocket: gps,
                            // chSocket: chs,
                            umsSocket: ums,
                            reportsSocket: reports,
                            oliveSocket: os,
                            invoicingSocket: is,
                            applicationSettingsSocket: ss,
                            isAuthenticated,
                            useAuth0
                        }
                    });

                    dispatch({ type:ActionTypes.CREATE_PROGRESS_MANAGER, payload: new ProgressManager( dispatch, state.accessToken! )})

                    return dispatch( {
                        type: ActionTypes.UPDATE_USER_DETAILS,
                        navigate,
                        notifier:props.Notifier,
                        payload: {
                            ...payload,
                            ...{
                                picture: payload && payload.picture
                                    ? payload.picture
                                    : null
                            }
                        } })
            })

            socket.on( "notify", (payload:any) => {

                let url = payload.url;

                const onClick = () => {
                    switch ( payload.type ) {
                        case "download":
                            window.open( url, "_blank" );
                            break;
                        default:
                            if( state.navigate ) {
                                props.Notifier.destroy()

                                const paths = [
                                    "https://www.mercury.sdl.com",
                                    "https://stage.mercury.sdl.com",
                                    "https://test.mercury.sdl.com",
                                    "https://dev.mercury.sdl.com",
                                    "http://localhost:3000"
                                ]

                                paths.forEach( ( path ) => {
                                    url = url.replace( path, "" );
                                } )
                                state.navigate( url );
                            }
                            break;
                    }
                }

                props.Notifier.info( {
                    key: payload._id,
                    message: payload.title,
                    description: payload.body,
                    duration: 500,
                    btn: (
                        <Button type="primary" size="small" onClick={onClick}>
                            { payload.type === "download" ? "Download" : "Show me" }
                        </Button>
                    )
                })

                socket.emit( "updateUserDetails" );
            } )

            socket.on( "logout", () => {
                dispatch( { type: ActionTypes.DELETE_TOKEN } );
            })

            socket.on("connect", () => {
                socket.emit("updateUserDetails");
            });

            socket.emit("updateUserDetails");

            return (
                <Auth0Wrapper {...props }>
                    <Loader logo={true} animate={true} title={"Creating connection..."} />
                </Auth0Wrapper>
            )
        }
        catch ( e ) {
            console.error( "Authentication error", e );
        }

        return (
            <Auth0Wrapper {...props }>
                <Loader logo={true} animate={true} />
            </Auth0Wrapper>
        )
    }


    if (  !LDClient ) {
        return (
            <Auth0Wrapper {...props }>
                <Loader logo={true} animate={true} title={"Initialising feature client..."} />
            </Auth0Wrapper>
        )
    }


    if ( state.accessToken && state.baseUrl && state.socket && !state.LD_Ready ) {

        if ( state.userDetails?.email ) {
            LDClient.identify( {
                key: state.userDetails.email,
                name: state.userDetails.firstName,
                email: state.userDetails.email
            }).then( () => {
                dispatch( { type: ActionTypes.LD_READY, payload: true } );
            }).catch( () => {
                dispatch( { type: ActionTypes.LD_READY, payload: true } );
            });
        }

        return (
            <Auth0Wrapper {...props }>
                <Loader logo={true} animate={true} title={"Waiting for features..."} />
            </Auth0Wrapper>
        )
    }

    if ( state.LD_Ready ) {

        if ( flags.holdingPage ) {

            const
                { startTime } = JSON.parse( flags.holdingPage ),
                start = new Date( startTime ),
                now = new Date();

            if ( now.getTime() >= start.getTime() ) {
                return <HoldingPage flags={flags} />
            }
        }

        return (
            <Auth0Wrapper {...props }>
                <MercuryContext.Provider
                    value={{
                        state: { ...state, selectedApp: state.selectedApp },
                        dispatch,
                        setSelectedApp,
                    }}
                    {...props}
                />
            </Auth0Wrapper>
        )
    }


    return (
        <Auth0Wrapper {...props }>
            <Loader logo={true} animate={true} title={"Loading application..."}/>
        </Auth0Wrapper>
    )
}

interface Auth0WrapperProps {
    REACT_APP_AUTH_AUDIENCE:string;
    REACT_APP_AUTH_CLIENT_ID:string;
    REACT_APP_AUTH_DOMAIN:string;
}

function Auth0Wrapper(props: React.PropsWithChildren<Auth0WrapperProps>) {

    const { children } = props;

    const {
        REACT_APP_AUTH_AUDIENCE,
        REACT_APP_AUTH_CLIENT_ID,
        REACT_APP_AUTH_DOMAIN
    } = props;

    const navigate = useNavigate();

    const onRedirectCallback = (appState: any) => {
        navigate(appState?.returnTo || window.location.pathname);
    };

    // user is already authenticated in the app.
    const context = useMercuryContext();
    const token = context?.accessToken;
    const isTokenValid = verifyToken(token);

     // If MAS token is valid use first before Auth0
    if (isTokenValid) {
        return <>{children}</>;
    }

    // If MAS Token is missing, expired, or invalid use Auth0
    return (
        <Auth0Provider
            domain={REACT_APP_AUTH_DOMAIN}
            clientId={REACT_APP_AUTH_CLIENT_ID}
            // cacheLocation="localstorage"
            // useRefreshTokens={true}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: REACT_APP_AUTH_AUDIENCE,
            }}
            onRedirectCallback={onRedirectCallback}
        >
            {children}
        </Auth0Provider>
    );
}

const LogoutRedirect = () => {
    useEffect(() => {
        Cookies.remove("mercury_shared_token", { path: "/" });
        Cookies.set("logged_out", "true", { path: "/" });

        sessionStorage.clear();
    }, []);

    return <LogoutPage />
}

function AuthProvider( props:React.PropsWithChildren<any>) {
    return (
        <Routes>
            <Route
                path="/logout"
                element={<LogoutRedirect />}
            />
            <Route
                path="*"
                element={<ContextProvider {...props } />} />
        </Routes>
    )
}

function useMercuryContext() {
    const { state } = React.useContext(MercuryContext);
    return state;
}

function useMercuryDispatch() {
    const { dispatch } = React.useContext(MercuryContext);
    return dispatch;
}

export const MercuryContextProvider = withLDProvider({
    clientSideID: config.LD_ID,
})(AuthProvider)

export {
    MercuryContext,
    useMercuryContext,
    useMercuryDispatch,
    Route,
    Link,
    useLocation
};
